import type { Readable, Unsubscriber, Writable } from "svelte/store";
import { derived, get, readable, writable } from "svelte/store";
import deepEqual from "fast-deep-equal";
import { AvailabilityStatus } from "@workadventure/messages";
import { AbortError } from "@workadventure/shared-utils/src/Abort/AbortError";
import * as Sentry from "@sentry/svelte";
import type { VideoQualitySetting } from "../Connection/LocalUserStore";
import { localUserStore } from "../Connection/LocalUserStore";
import { Room } from "../Connection/Room";
import { analyticsClient } from "../Administration/AnalyticsClient";
import { openTimedEventPerMeeting } from "../Administration/CurrentMeeting";
import type { EndTimedAnalyticsEvent } from "../Administration/TimedAnalyticsEvent";
import { createHeldIntervalTracker } from "../Administration/HeldIntervalTracker";
import { isIOS } from "../WebRtc/DeviceUtils";
import { SoundMeter } from "../Phaser/Components/SoundMeter";
import type { RequestedStatus } from "../Rules/StatusRules/statusRules";
import { statusChanger } from "../Components/ActionBar/AvailabilityStatus/statusChanger";
import {
    BackgroundProcessingUnsupportedError,
    type BackgroundConfig,
    type BackgroundMode,
    type BackgroundTransformer,
    createBackgroundTransformer,
} from "../WebRtc/BackgroundProcessor/createBackgroundTransformer";
import { LL } from "../../i18n/i18n-svelte";
import { currentRoomStore } from "./CurrentRoomStore";
import { gameSceneIsLoadedStore } from "./GameSceneStore";
import { MediaStreamConstraintsError } from "./Errors/MediaStreamConstraintsError";
import { BrowserTooOldError } from "./Errors/BrowserTooOldError";
import { errorStore, warningMessageStore } from "./ErrorStore";
import { WebviewOnOldIOS } from "./Errors/WebviewOnOldIOS";

import { createSilentStore } from "./SilentStore";
import { privacyShutdownStore } from "./PrivacyShutdownStore";
import { inExternalServiceStore, myCameraStore, myMicrophoneStore, proximityMeetingStore } from "./MyMediaStore";
import { userMovingStore } from "./GameStore";
import { hideHelpCameraSettings } from "./HelpSettingsStore";
import { isLiveStreamingAudioStore, isLiveStreamingStore } from "./IsStreamingStore";
import { currentPlayerGroupIdStore } from "./CurrentPlayerGroupStore";

import { backgroundConfigStore, backgroundProcessingEnabledStore } from "./BackgroundTransformStore";
import {
    browserNoiseSuppressionSupportedStore,
    customNoiseSuppressionActiveStore,
    effectiveNoiseSuppressionProviderStore,
    microphoneAutoGainControlStore,
    microphoneBrowserNoiseSuppressionStore,
    microphoneEchoCancellationStore,
    noiseSuppressionEnabledStore,
    noiseSuppressionEngineStore,
    noiseSuppressionStateStore,
    voiceIsolationSupportedStore,
} from "./NoiseSuppressionStore";
import {
    composeLocalStreamValue,
    getLocalTrackFromStreamValue,
    type LocalStreamStoreValue,
    type LocalTrackStoreValue,
} from "./LocalStreamTypes";
import { NoiseSuppressionController } from "./NoiseSuppressionController";
import { buildMicrophoneAudioConstraints, isVoiceIsolationSupported } from "./MicrophoneSettings";
import { speakerSelectedStore, speakerSelectionSupported } from "./AudioOutputStore";
import { audioPlaybackStore } from "./AudioPlaybackStore";
import { browserNotificationStore } from "./BrowserNotificationStore";

export const inBackgroundSettingsStore = writable<boolean>(false);

export type MediaAccessIssue = "permission_denied" | "no_device";
type MediaDeviceAnalyticsKind = "camera" | "microphone" | "camera_microphone";

/**
 * Last camera access failure, or no videoinput reported by the browser.
 * Cleared when the user turns the camera off manually, when a video track is obtained, or when a camera appears.
 */
export const cameraAccessIssueStore = writable<MediaAccessIssue | null>(null);

/**
 * Last microphone access failure, or no audioinput reported by the browser.
 * Cleared when the user turns the microphone off manually, when an audio track is obtained, or when a microphone appears.
 */
export const microphoneAccessIssueStore = writable<MediaAccessIssue | null>(null);

/**
 * A store that contains the camera state requested by the user (on or off).
 */
function createRequestedCameraState() {
    const { subscribe, set } = writable(localUserStore.getRequestedCameraState());

    return {
        subscribe,
        enableWebcam: () => {
            set(true);
            localUserStore.setRequestedCameraState(true);
        },
        disableWebcam: () => {
            set(false);
            localUserStore.setRequestedCameraState(false);
        },
    };
}

/**
 * A store that contains the microphone state requested by the user (on or off).
 */
function createRequestedMicrophoneState() {
    const { subscribe, set } = writable(localUserStore.getRequestedMicrophoneState());

    return {
        subscribe,
        enableMicrophone: () => {
            set(true);
            localUserStore.setRequestedMicrophoneState(true);
        },
        disableMicrophone: () => {
            set(false);
            localUserStore.setRequestedMicrophoneState(false);
        },
    };
}

/**
 * A store that contains whether the EnableCameraScene is shown or not.
 */
function createEnableCameraSceneVisibilityStore() {
    const { subscribe, set } = writable(false);

    return {
        subscribe,
        showEnableCameraScene: () => set(true),
        hideEnableCameraScene: () => set(false),
    };
}

export const requestedCameraState = createRequestedCameraState();
export const requestedMicrophoneState = createRequestedMicrophoneState();
export const enableCameraSceneVisibilityStore = createEnableCameraSceneVisibilityStore();

/**
 * A store that is true when the megaphone screen is displayed.
 */
export const displayedMegaphoneScreenStore = writable<boolean>(false);

/**
 * GetUserMedia is impacted by a number of stores (proximityMeetingStore, myCameraStore, myMicrophoneStore, inExternalServiceStore, privacyShutdownStore...).
 * Each time a change is done to one of these store, we will make a new GetUserMedia call.
 * If we plan to do many changes at once, we want to call GetUserMedia only once.
 *
 * To do this, you can use this store.
 * Use startBatch() to start a batch of changes (this will disable changes to GetUserMedia), and commitChanges() after the final change to call GetUserMedia.
 */
function createBatchGetUserMediaStore() {
    const { subscribe, set } = writable(false);

    return {
        subscribe,
        startBatch: () => set(true),
        commitChanges: () => set(false),
    };
}

export const batchGetUserMediaStore = createBatchGetUserMediaStore();

/**
 * A store containing whether the webcam was enabled in the last 10 seconds
 */
const enabledWebCam10secondsAgoStore = readable(false, function start(set) {
    let timeout: NodeJS.Timeout | null = null;

    const unsubscribe = requestedCameraState.subscribe((enabled) => {
        if (enabled === true) {
            if (timeout) {
                clearTimeout(timeout);
            }
            timeout = setTimeout(() => {
                set(false);
            }, 10000);
            set(true);
        } else {
            set(false);
        }
    });

    return function stop() {
        if (timeout) {
            clearTimeout(timeout);
        }
        unsubscribe();
    };
});

/**
 * A store containing whether the webcam was enabled in the last 5 seconds
 */
const userMoved5SecondsAgoStore = readable(false, function start(set) {
    let timeout: NodeJS.Timeout | null = null;

    const unsubscribe = userMovingStore.subscribe((moving) => {
        if (moving === true) {
            if (timeout) {
                clearTimeout(timeout);
            }
            set(true);
        } else {
            timeout = setTimeout(() => {
                set(false);
            }, 5000);
        }
    });

    return function stop() {
        unsubscribe();
    };
});

/**
 * A store awaiting the loading of devices information.
 */
export const devicesNotLoaded = writable(true);

const deviceChanged10SecondsAgoStore = readable(false, function start(set) {
    let timeout: NodeJS.Timeout | null = null;

    const unsubscribeCamera = videoConstraintStore.subscribe((constraints) => {
        if (timeout) {
            clearTimeout(timeout);
        }
        set(true);
        timeout = setTimeout(() => {
            set(false);
        }, 10000);
    });

    const unsubscribeAudio = audioConstraintStore.subscribe((constraints) => {
        if (timeout) {
            clearTimeout(timeout);
        }
        set(true);
        timeout = setTimeout(() => {
            set(false);
        }, 10000);
    });

    return function stop() {
        unsubscribeCamera();
        unsubscribeAudio();
    };
});

/**
 * A store containing if the mouse is over the camera button
 */
export const mouseIsHoveringCameraButton = writable(false);

export const cameraNoEnergySavingStore = writable<boolean>(false);

export const streamingMegaphoneStore = writable<boolean>(false);
export const inJitsiStore = writable(false);
export const inBbbStore = writable(false);
export const isSpeakerStore = writable(false);
export const inLivekitStore = writable(false);
export const isListenerStore = writable(false);
export const listenerWaitingMediaStore = writable<string | undefined>(undefined);
/**
 * When true, the listener has consented to share their camera with the speaker (seeAttendees feature).
 * This store is set to true when the listener accepts the camera sharing popup.
 */
export const listenerSharingCameraStore = writable(false);

export const requestedCameraDeviceIdStore: Writable<string | undefined> = writable(
    localUserStore.getPreferredVideoInputDevice() ? localUserStore.getPreferredVideoInputDevice() : undefined,
);

export const frameRateStore: Writable<number | undefined> = writable();
export const requestedMicrophoneDeviceIdStore: Writable<string | undefined> = writable(
    localUserStore.getPreferredAudioInputDevice() ? localUserStore.getPreferredAudioInputDevice() : undefined,
);

export const usedCameraDeviceIdStore: Writable<string | undefined> = writable();
export const usedMicrophoneDeviceIdStore: Writable<string | undefined> = writable();

export const inOpenWebsite = writable(false);

/**
 * A store that contains video constraints.
 */
export const videoConstraintStore = derived(
    [requestedCameraDeviceIdStore, frameRateStore],
    ([$cameraDeviceIdStore, $frameRateStore]) => {
        const constraints = {
            width: { ideal: 1280 },
            height: { ideal: 720 },
            frameRate: { ideal: 30 },
            facingMode: "user",
            resizeMode: "crop-and-scale",
            aspectRatio: 1.777777778,

            // Uncomment the lines below to simulate a mobile device
            //height: { min: 640, ideal: 1280, max: 1920 },
            //width: { min: 400, ideal: 720, max: 1080 },
            //resizeMode: "none",
        } as MediaTrackConstraints;

        if ($cameraDeviceIdStore !== undefined) {
            constraints.deviceId = {
                exact: $cameraDeviceIdStore,
            };
        }
        if ($frameRateStore !== undefined) {
            constraints.frameRate = { ideal: $frameRateStore };
        }

        return constraints;
    },
);

/**
 * A store that contains audio constraints.
 */
export const audioConstraintStore = derived(
    [
        requestedMicrophoneDeviceIdStore,
        microphoneAutoGainControlStore,
        microphoneEchoCancellationStore,
        microphoneBrowserNoiseSuppressionStore,
        noiseSuppressionEnabledStore,
        effectiveNoiseSuppressionProviderStore,
        noiseSuppressionEngineStore,
        browserNoiseSuppressionSupportedStore,
        customNoiseSuppressionActiveStore,
        noiseSuppressionStateStore,
    ],
    ([
        $microphoneDeviceIdStore,
        $microphoneAutoGainControlStore,
        $microphoneEchoCancellationStore,
        $microphoneBrowserNoiseSuppressionStore,
        $noiseSuppressionEnabledStore,
        $effectiveNoiseSuppressionProviderStore,
        $noiseSuppressionEngineStore,
        $browserNoiseSuppressionSupportedStore,
        $customNoiseSuppressionActiveStore,
        $noiseSuppressionStateStore,
    ]) => {
        const supportedConstraints = navigator.mediaDevices?.getSupportedConstraints();
        let constraints: boolean | MediaTrackConstraints = buildMicrophoneAudioConstraints({
            microphoneDeviceId: $microphoneDeviceIdStore,
            autoGainControl: $microphoneAutoGainControlStore,
            echoCancellation: $microphoneEchoCancellationStore,
            noiseSuppressionEnabled: $noiseSuppressionEnabledStore,
            browserNoiseSuppressionEnabled: $microphoneBrowserNoiseSuppressionStore,
            effectiveNoiseSuppressionProvider: $effectiveNoiseSuppressionProviderStore,
            noiseSuppressionEngine: $noiseSuppressionEngineStore,
            browserNoiseSuppressionSupported: $browserNoiseSuppressionSupportedStore,
            workAdventureNoiseSuppressionFailed:
                $noiseSuppressionStateStore.status === "error" || $noiseSuppressionStateStore.status === "unsupported",
            customNoiseSuppressionActive: $customNoiseSuppressionActiveStore,
            voiceIsolationAdvertised: supportedConstraints?.voiceIsolation === true,
            deviceIdSupported: supportedConstraints?.deviceId === true,
            sampleRateSupported: supportedConstraints?.sampleRate === true,
        });

        if (typeof constraints === "boolean") {
            constraints = {};
        }
        return constraints;
    },
);

/**
 * A store that contains "true" if the webcam should be stopped for energy efficiency reason - i.e. we are not moving and not in a conversation.
 */
export const cameraEnergySavingStore = derived(
    [
        deviceChanged10SecondsAgoStore,
        userMoved5SecondsAgoStore,
        enabledWebCam10secondsAgoStore,
        mouseIsHoveringCameraButton,
        cameraNoEnergySavingStore,
        devicesNotLoaded,
        isLiveStreamingStore,
        currentPlayerGroupIdStore,
        inLivekitStore,
        displayedMegaphoneScreenStore,
    ],
    ([
        $deviceChanged10SecondsAgoStore,
        $userMoved5SecondsAgoStore,
        $enabledWebCam10secondsAgoStore,
        $mouseInBottomRight,
        $cameraNoEnergySavingStore,
        $devicesNotLoaded,
        $isLiveStreamingStore,
        $currentPlayerGroupIdStore,
        $inLivekitStore,
        $displayedMegaphoneScreenStore,
    ]) => {
        return (
            !$mouseInBottomRight &&
            !$userMoved5SecondsAgoStore &&
            !$deviceChanged10SecondsAgoStore &&
            !$enabledWebCam10secondsAgoStore &&
            !$cameraNoEnergySavingStore &&
            !$devicesNotLoaded &&
            !$isLiveStreamingStore &&
            $currentPlayerGroupIdStore === undefined &&
            !$inLivekitStore &&
            !$displayedMegaphoneScreenStore
        );
    },
);

export const requestedStatusStore: Writable<RequestedStatus | null> = writable(localUserStore.getRequestedStatus());

export const inCowebsiteZone = derived(
    [inJitsiStore, inBbbStore, inOpenWebsite],
    ([$inJitsiStore, $inBbbStore, $inOpenWebsite]) => {
        return $inJitsiStore || $inBbbStore || $inOpenWebsite;
    },
    false,
);

export const silentStore = createSilentStore();

export const availabilityStatusStore = derived(
    [
        inJitsiStore,
        inBbbStore,
        silentStore,
        privacyShutdownStore,
        proximityMeetingStore,
        isSpeakerStore,
        requestedStatusStore,
        inLivekitStore,
        isListenerStore,
        audioPlaybackStore,
        browserNotificationStore,
    ],
    ([
        $inJitsiStore,
        $inBbbStore,
        $silentStore,
        $privacyShutdownStore,
        $proximityMeetingStore,
        $isSpeakerStore,
        $requestedStatusStore,
        $inLivekitStore,
        $isListenerStore,
        $audioPlaybackStore,
        $browserNotificationStore,
    ]) => {
        // Important: Statuses that should not switch to BUSY
        // must be checked BEFORE privacyShutdownStore to prevent switching to BUSY when privacy is enabled.
        if ($audioPlaybackStore.size > 0 && !$browserNotificationStore) return AvailabilityStatus.SOUND_BLOCKED;
        if ($inJitsiStore) return AvailabilityStatus.JITSI;
        if ($inBbbStore) return AvailabilityStatus.BBB;
        if (!$proximityMeetingStore) return AvailabilityStatus.DENY_PROXIMITY_MEETING;
        if ($isSpeakerStore) return AvailabilityStatus.SPEAKER;
        if ($silentStore) return AvailabilityStatus.SILENT;
        if ($inLivekitStore) return AvailabilityStatus.LIVEKIT;
        if ($isListenerStore) return AvailabilityStatus.LISTENER;
        if ($requestedStatusStore) return $requestedStatusStore;
        if ($privacyShutdownStore) return AvailabilityStatus.AWAY;

        return AvailabilityStatus.ONLINE;
    },
    AvailabilityStatus.ONLINE,
);

/**
 * The period the user has been in their current status, and which status that is.
 *
 * The name is kept because availabilityStatusStore is derived and re-emits the same
 * value: without it, every recomputation would end one dwell and start another.
 */
let endStatusDwell: EndTimedAnalyticsEvent | undefined;
let currentStatusName: string | undefined;

// This is a singleton so we can safely not ever unsubscribe from it.
// eslint-disable-next-line svelte/no-ignored-unsubscribe
availabilityStatusStore.subscribe((newStatus: AvailabilityStatus) => {
    // Time-in-status is reported as a `status.dwell` timed event, gated per world by
    // the `user_level_activity` policy the admin applies at ingestion: without opt-in
    // it is pseudonymized there, so no named per-member timeline is stored. The enum
    // key name ("ONLINE", …) is sent, low-cardinality and non-PII, so it survives.
    const statusName = AvailabilityStatus[newStatus] ?? String(newStatus);
    if (statusName !== currentStatusName) {
        currentStatusName = statusName;
        endStatusDwell?.();
        endStatusDwell = analyticsClient.openTimedEvent(
            "status.dwell",
            { status: statusName },
            // The status did not change because the socket did: after a reconnect the
            // user is still Busy, and nothing will say so again.
            { reopenOnReconnect: true },
        );
    }
    try {
        statusChanger.changeStatusTo(newStatus);
    } catch (e) {
        console.error("Error while changing status", e);
        Sentry.captureException(e);
    }
});

let previousComputedVideoConstraint: boolean | MediaTrackConstraints = false;
let previousComputedAudioConstraint: boolean | MediaTrackConstraints = false;
let previousComputedKeepAudioWarm = false;

export interface MediaStreamConstraintsValue {
    video: false | MediaTrackConstraints;
    audio: false | MediaTrackConstraints;
    /**
     * True when the microphone is off only because the user muted it during a conversation: the audio track
     * is then kept open (disabled) so that unmuting does not have to reopen the device.
     * Reopening it takes 0.3 to 1 s, and 1 to 3 s with Bluetooth headsets that switch from their music profile
     * (A2DP) to their call profile (HFP). People start talking as soon as they click unmute, so their first words
     * were lost.
     */
    keepAudioWarm: boolean;
}

/**
 * A store containing the media constraints we want to apply.
 */
export const mediaStreamConstraintsStore = derived(
    [
        requestedCameraState,
        requestedMicrophoneState,
        myCameraStore,
        myMicrophoneStore,
        inExternalServiceStore,
        enableCameraSceneVisibilityStore,
        videoConstraintStore,
        audioConstraintStore,
        privacyShutdownStore,
        currentRoomStore,
        cameraEnergySavingStore,
        availabilityStatusStore,
        batchGetUserMediaStore,
        inBackgroundSettingsStore,
        isLiveStreamingAudioStore,
    ],
    (
        [
            $requestedCameraState,
            $requestedMicrophoneState,
            $myCameraStore,
            $myMicrophoneStore,
            $inExternalServiceStore,
            $enableCameraSceneVisibilityStore,
            $videoConstraintStore,
            $audioConstraintStore,
            $privacyShutdownStore,
            $currentRoomStore,
            $cameraEnergySavingStore,
            $availabilityStatusStore,
            $batchGetUserMediaStore,
            $inBackgroundSettingsStore,
            $isLiveStreamingAudioStore,
        ],
        set,
    ) => {
        // If a batch is in process, don't do anything.
        if ($batchGetUserMediaStore) {
            return;
        }

        let currentVideoConstraint: boolean | MediaTrackConstraints = $videoConstraintStore;
        let currentAudioConstraint: boolean | MediaTrackConstraints = $audioConstraintStore;

        // Shared conditions for disabling media
        const isInExternalService = $inExternalServiceStore === true;
        const isEnergySaving = $cameraEnergySavingStore === true && $enableCameraSceneVisibilityStore === false;
        const isUnavailableStatus =
            $availabilityStatusStore === AvailabilityStatus.DENY_PROXIMITY_MEETING ||
            $availabilityStatusStore === AvailabilityStatus.SILENT ||
            $availabilityStatusStore === AvailabilityStatus.DO_NOT_DISTURB ||
            $availabilityStatusStore === AvailabilityStatus.BACK_IN_A_MOMENT ||
            $availabilityStatusStore === AvailabilityStatus.SOUND_BLOCKED ||
            $availabilityStatusStore === AvailabilityStatus.BUSY;
        const shouldDisableMicrophoneForPrivacy =
            $privacyShutdownStore === true &&
            !localUserStore.getMicrophonePrivacySettings(
                $currentRoomStore?.defaultMicrophonePrivacySettings ?? Room.DEFAULT_MICROPHONE_PRIVACY_SETTINGS,
            );
        const shouldDisableCameraForPrivacy =
            $privacyShutdownStore === true &&
            !localUserStore.getCameraPrivacySettings(
                $currentRoomStore?.defaultCameraPrivacySettings ?? Room.DEFAULT_CAMERA_PRIVACY_SETTINGS,
            );

        // Every reason, other than the user muting it, to cut the microphone
        const isMicrophoneForcedOff =
            $myMicrophoneStore === false ||
            isInExternalService ||
            shouldDisableMicrophoneForPrivacy ||
            isEnergySaving ||
            isUnavailableStatus;

        // Audio constraints always apply
        if ($requestedMicrophoneState === false || isMicrophoneForcedOff) {
            currentAudioConstraint = false;
        }

        // Unmuting must be instant during a conversation: reopening the microphone with getUserMedia takes
        // 1 to 3 seconds (much more with Bluetooth headsets switching to their call profile).
        // Only a mute by the user keeps the track open; every other reason really releases the device.
        // A space streams our audio in a bubble, a meeting area, or a speaker zone where we are a speaker
        // (not a mere listener, who cannot be heard anyway).
        const keepAudioWarm =
            $isLiveStreamingAudioStore && $requestedMicrophoneState === false && !isMicrophoneForcedOff;

        // Video constraints only apply when NOT in background settings (to allow camera preview)
        if (!$inBackgroundSettingsStore) {
            if (
                $requestedCameraState === false ||
                $myCameraStore === false ||
                isInExternalService ||
                shouldDisableCameraForPrivacy ||
                isEnergySaving ||
                isUnavailableStatus
            ) {
                currentVideoConstraint = false;
            }
        }
        // Let's make the changes only if the new value is different from the old one.
        if (
            !deepEqual(previousComputedVideoConstraint, currentVideoConstraint) ||
            !deepEqual(previousComputedAudioConstraint, currentAudioConstraint) ||
            previousComputedKeepAudioWarm !== keepAudioWarm
        ) {
            previousComputedVideoConstraint = currentVideoConstraint;
            previousComputedAudioConstraint = currentAudioConstraint;
            previousComputedKeepAudioWarm = keepAudioWarm;
            // Let's copy the objects.
            if (typeof previousComputedVideoConstraint !== "boolean") {
                previousComputedVideoConstraint = { ...previousComputedVideoConstraint };
            }
            if (typeof previousComputedAudioConstraint !== "boolean") {
                previousComputedAudioConstraint = { ...previousComputedAudioConstraint };
            }

            set({
                video: currentVideoConstraint,
                audio: currentAudioConstraint,
                keepAudioWarm,
            });
        }
    },
    {
        video: false,
        audio: false,
        keepAudioWarm: false,
    } as MediaStreamConstraintsValue,
);

export type { LocalStreamStoreValue } from "./LocalStreamTypes";

let currentStream: MediaStream | undefined = undefined;
/**
 * The microphone track muted during a conversation, kept open but disabled (see keepAudioWarm).
 * It is out of currentStream, so the rest of the application sees the microphone as off.
 */
let warmAudio: { track: MediaStreamTrack; constraints: MediaTrackConstraints } | undefined = undefined;

function releaseWarmAudio(): void {
    warmAudio?.track.stop();
    warmAudio = undefined;
}

/**
 * Returns the warm microphone track if it can serve the requested audio constraints, re-enabled.
 */
function takeWarmAudio(audioConstraints: false | MediaTrackConstraints): MediaStreamTrack | undefined {
    if (
        warmAudio === undefined ||
        audioConstraints === false ||
        warmAudio.track.readyState !== "live" ||
        !deepEqual(warmAudio.constraints, audioConstraints)
    ) {
        return undefined;
    }
    const track = warmAudio.track;
    warmAudio = undefined;
    track.enabled = true;
    return track;
}

let oldConstraints: { video: MediaTrackConstraints | false; audio: MediaTrackConstraints | false } = {
    video: false,
    audio: false,
};
// Use the factory to create the appropriate transformer
let backgroundTransformer: BackgroundTransformer | undefined = undefined;
// Track the last background config to detect if we need to recreate or just update
let lastBackgroundConfig: BackgroundConfig | undefined = undefined;
// The running background pipe: the camera track it reads and the track it outputs. The raw stream is rebuilt
// whenever the microphone changes; when it still carries the same camera track, the pipe is kept instead of
// being torn down and rebuilt for nothing.
let backgroundPipe: { input: MediaStreamTrack; output: MediaStreamTrack | undefined } | undefined = undefined;
// AbortController for the in-flight transform; aborted when a new run is scheduled
let currentTransformAbortController: AbortController | null = null;
// AbortController for the in-flight noise suppression transform; aborted when a new run is scheduled
let currentAudioProcessedTransformAbortController: AbortController | null = null;
const noiseSuppressionController = new NoiseSuppressionController();

/**
 * Update background processor configuration without recreating the transformer
 */
export function updateBackgroundProcessor(config: {
    blurAmount?: number;
    backgroundImage?: string;
    mode?: BackgroundMode;
}) {
    if (backgroundTransformer && backgroundTransformer.updateConfig) {
        try {
            backgroundTransformer
                .updateConfig({
                    mode: config.mode,
                    blurAmount: config.blurAmount,
                    backgroundImage: config.backgroundImage,
                })
                .catch((error) => {
                    console.warn("[MediaStore] Failed to update background transformer configuration:", error);
                });

            // Update the tracked config
            if (lastBackgroundConfig && config.mode) {
                lastBackgroundConfig.mode = config.mode;
            }
            if (lastBackgroundConfig && config.blurAmount !== undefined) {
                lastBackgroundConfig.blurAmount = config.blurAmount;
            }
            if (lastBackgroundConfig && config.backgroundImage !== undefined) {
                lastBackgroundConfig.backgroundImage = config.backgroundImage;
            }
        } catch (error) {
            console.warn("[MediaStore] Failed to update background transformer configuration:", error);
        }
    }
}

/**
 * Serializes raw local stream updates so that only one getUserMedia flow runs at a time
 * and the last enqueued update wins (stale completions do not overwrite the store).
 */
let rawStreamGeneration = 0;
let rawStreamUpdateQueue: Promise<void> = Promise.resolve();

type SetRawStreamIfCurrent = (value: LocalStreamStoreValue) => void;

function hasLiveTrack(tracks: MediaStreamTrack[]): boolean {
    return tracks.some((track) => track.readyState === "live");
}

function classifyMediaAccessError(error: unknown): MediaAccessIssue | null {
    const name = error instanceof Error ? error.name : "";
    if (name === "NotAllowedError" || name === "PermissionDeniedError" || name === "SecurityError") {
        return "permission_denied";
    }
    if (name === "NotFoundError" || name === "DevicesNotFoundError") {
        return "no_device";
    }
    return null;
}

function trackMediaAccessIssue(kind: MediaDeviceAnalyticsKind, issue: MediaAccessIssue | null): void {
    if (issue === "permission_denied") {
        analyticsClient.trackAdminEvent("media.permission_denied", { kind, reason: issue });
    } else if (issue === "no_device") {
        analyticsClient.trackAdminEvent("media.device_error", { kind, reason: issue });
    }
}

function emitCurrentStreamOrError(setIfCurrent: SetRawStreamIfCurrent, error: unknown) {
    if (currentStream) {
        setIfCurrent({
            type: "success",
            stream: currentStream,
        });
        return;
    }

    setIfCurrent({
        type: "error",
        error: error instanceof Error ? error : new Error("An unknown error happened"),
    });
}

async function runRawStreamUpdate(
    constraints: MediaStreamConstraintsValue,
    setIfCurrent: SetRawStreamIfCurrent,
    generation: number,
): Promise<{ video: false | MediaTrackConstraints; audio: false | MediaTrackConstraints }> {
    if (navigator.mediaDevices === undefined) {
        analyticsClient.trackAdminEvent("media.device_error", {
            kind: "camera_microphone",
            reason: "media_devices_unavailable",
        });
        if (window.location.protocol === "http:") {
            setIfCurrent({
                type: "error",
                error: new Error("Unable to access your camera or microphone. You need to use a HTTPS connection."),
            });
            return constraints;
        }
        if (isIOS()) {
            setIfCurrent({
                type: "error",
                error: new WebviewOnOldIOS(),
            });
            return constraints;
        }
        setIfCurrent({
            type: "error",
            error: new BrowserTooOldError(),
        });
        return constraints;
    }

    if (currentStream === undefined) {
        setIfCurrent({
            type: "success",
            stream: undefined,
        });
    }

    cameraAccessIssueStore.set(null);
    microphoneAccessIssueStore.set(null);

    const nextConstraints = {
        video: constraints.video ?? false,
        audio: constraints.audio ?? false,
    };

    const warmAudioTrack = takeWarmAudio(constraints.audio);
    // Keep the warm track only while the microphone stays muted in the conversation. Otherwise, release it
    // before any getUserMedia call (see the Chromium issue below).
    if (constraints.audio !== false || !constraints.keepAudioWarm) {
        releaseWarmAudio();
    }

    const hasLiveVideoTrack = currentStream ? hasLiveTrack(currentStream.getVideoTracks()) : false;
    const hasLiveAudioTrack = currentStream ? hasLiveTrack(currentStream.getAudioTracks()) : false;
    const mustRequestNewVideo =
        constraints.video !== false && (!deepEqual(oldConstraints.video, constraints.video) || !hasLiveVideoTrack);
    const mustRequestNewAudio =
        constraints.audio !== false &&
        warmAudioTrack === undefined &&
        (!deepEqual(oldConstraints.audio, constraints.audio) || !hasLiveAudioTrack);

    if (currentStream) {
        const oldStream = currentStream;
        const mustStopVideo = oldConstraints.video !== false && constraints.video === false;
        const oldAudioConstraints = oldConstraints.audio;
        const mustStopAudio = oldAudioConstraints !== false && constraints.audio === false;

        if (mustStopVideo) {
            oldStream.getVideoTracks().forEach((t) => {
                t.stop();
                oldStream.removeTrack(t);
            });
        }
        if (mustStopAudio) {
            oldStream.getAudioTracks().forEach((t) => {
                oldStream.removeTrack(t);
                if (constraints.keepAudioWarm && t.readyState === "live") {
                    releaseWarmAudio();
                    t.enabled = false;
                    warmAudio = { track: t, constraints: oldAudioConstraints };
                } else {
                    t.stop();
                }
            });
        }
        if (mustStopVideo || mustStopAudio) {
            setIfCurrent({
                type: "success",
                stream: oldStream,
            });
        }
    }

    if (warmAudioTrack) {
        currentStream = new MediaStream([
            ...(currentStream?.getVideoTracks().filter((track) => track.readyState !== "ended") ?? []),
            warmAudioTrack,
        ]);
        setIfCurrent({
            type: "success",
            stream: currentStream,
        });
    }

    if (mustRequestNewVideo || mustRequestNewAudio) {
        const newConstraints: MediaStreamConstraints = {};
        if (mustRequestNewVideo) {
            newConstraints.video = constraints.video;
        } else {
            newConstraints.video = false;
        }
        if (mustRequestNewAudio) {
            newConstraints.audio = constraints.audio;
        } else {
            newConstraints.audio = false;
        }

        // Note: we need to stop the tracks BEFORE calling getUserMedia and not after because of a Chromium issue:
        // If some settings are modified (like autoGainControl), they can be ignored if a track already exists
        // for the same microphone, but with different settings.
        if (currentStream) {
            if (mustRequestNewVideo) {
                currentStream.getVideoTracks().forEach((track) => {
                    track.stop();
                });
            }
            if (mustRequestNewAudio) {
                currentStream.getAudioTracks().forEach((track) => {
                    track.stop();
                });
            }
        }

        try {
            const stream = await navigator.mediaDevices.getUserMedia(newConstraints);
            if (generation !== rawStreamGeneration) {
                // A newer update is queued/running: discard this stale stream so devices are released immediately.
                // The returned constraints are ignored by caller for stale generations.
                stream.getTracks().forEach((track) => track.stop());
                return nextConstraints;
            }
            const oldStream = currentStream;
            currentStream =
                oldStream === undefined
                    ? stream
                    : new MediaStream([
                          ...(!mustRequestNewVideo
                              ? oldStream.getVideoTracks().filter((track) => track.readyState !== "ended")
                              : []),
                          ...(!mustRequestNewAudio
                              ? oldStream.getAudioTracks().filter((track) => track.readyState !== "ended")
                              : []),
                          ...stream.getTracks(),
                      ]);
            setIfCurrent({
                type: "success",
                stream: currentStream,
            });
            batchGetUserMediaStore.startBatch();
            if (currentStream.getVideoTracks().length > 0) {
                usedCameraDeviceIdStore.set(currentStream.getVideoTracks()[0]?.getSettings().deviceId);
            }
            if (currentStream.getAudioTracks().length > 0) {
                const audioTrack = currentStream.getAudioTracks()[0];
                const audioTrackSettings = audioTrack?.getSettings();
                usedMicrophoneDeviceIdStore.set(audioTrackSettings?.deviceId);
                voiceIsolationSupportedStore.setSupported(
                    isVoiceIsolationSupported(audioTrackSettings, audioTrack?.getCapabilities?.()),
                );
            } else {
                voiceIsolationSupportedStore.setSupported(false);
            }
            batchGetUserMediaStore.commitChanges();
            hideHelpCameraSettings();
        } catch (e) {
            if (isOverConstrainedError(e) && e.constraint === "deviceId") {
                analyticsClient.trackAdminEvent("media.device_error", {
                    kind: "camera_microphone",
                    reason: "device_constraint",
                });
                console.info(
                    "Could not access the requested microphone or webcam. Falling back to default microphone and webcam",
                    newConstraints,
                    e,
                );
                batchGetUserMediaStore.startBatch();
                requestedCameraDeviceIdStore.set(undefined);
                requestedMicrophoneDeviceIdStore.set(undefined);
                batchGetUserMediaStore.commitChanges();
            } else if (mustRequestNewVideo) {
                console.info(
                    "Error. Unable to get microphone and/or camera access. Trying audio only.",
                    newConstraints,
                    e,
                );
                emitCurrentStreamOrError(setIfCurrent, e);
                const classified = classifyMediaAccessError(e);
                trackMediaAccessIssue(mustRequestNewAudio ? "camera_microphone" : "camera", classified);
                // Only the camera: turning it off asks for the microphone alone, which turns the microphone off in
                // its own turn if it fails too. A camera taken by another application (NotReadableError on Windows)
                // must not cost the microphone.
                requestedCameraState.disableWebcam();
                cameraAccessIssueStore.set(classified);
            } else if (!constraints.video && !constraints.audio) {
                console.error("Error. getUserMedia called with no audio and no video.");
                setIfCurrent({
                    type: "error",
                    error: new MediaStreamConstraintsError(),
                });
            } else {
                console.info("Error. Unable to get microphone and/or camera access.", newConstraints, e);
                emitCurrentStreamOrError(setIfCurrent, e);
                if (mustRequestNewAudio) {
                    trackMediaAccessIssue("microphone", classifyMediaAccessError(e));
                    requestedMicrophoneState.disableMicrophone();
                    microphoneAccessIssueStore.set(classifyMediaAccessError(e));
                }
            }
        }
    }

    return nextConstraints;
}

/**
 * In case a device is removed, we need to retry the getUserMedia call to get another device if available.
 */
const userMediaRetryCountStore = writable(0);

/**
 * Triggers a new call to getUserMedia to refresh the stream.
 * Useful when a default device has been removed.
 */
function retryGetUserMedia(retryVideo: boolean, retryAudio: boolean) {
    if (retryVideo) {
        oldConstraints.video = false;
    }
    if (retryAudio) {
        oldConstraints.audio = false;
    }
    userMediaRetryCountStore.update((count) => count + 1);
}

/**
 * A store containing the raw MediaStream object (or undefined if nothing requested, or Error if an error occurred)
 *
 * NOTE: We depend on forceTransformerRecreationStore to detect when mode changes require recreation.
 * Parameter changes (blurAmount, etc.) are handled by a separate subscriber to avoid recreating
 * the transformer on every change (which causes WebGL context leaks).
 */
export const rawLocalStreamStore = derived<
    [typeof mediaStreamConstraintsStore, typeof userMediaRetryCountStore],
    LocalStreamStoreValue
>(
    [mediaStreamConstraintsStore, userMediaRetryCountStore],
    ([$mediaStreamConstraintsStore, $userMediaRetryCountStore], set) => {
        const constraints = { ...$mediaStreamConstraintsStore };
        const myGen = ++rawStreamGeneration;
        const setIfCurrent: SetRawStreamIfCurrent = (value) => {
            if (myGen === rawStreamGeneration) {
                set(value);
            }
        };

        rawStreamUpdateQueue = rawStreamUpdateQueue
            .then(async () => {
                const result = await runRawStreamUpdate(constraints, setIfCurrent, myGen);
                if (myGen === rawStreamGeneration) {
                    oldConstraints = result;
                }
            })
            .catch((e) => {
                console.error("Error in raw local stream update queue", e);
                setIfCurrent({
                    type: "error",
                    error: e instanceof Error ? e : new Error("An unknown error happened"),
                });
            });
    },
    {
        type: "success",
        stream: undefined,
    },
);

export const rawLocalAudioTrackStore = derived<typeof rawLocalStreamStore, LocalTrackStoreValue>(
    rawLocalStreamStore,
    ($rawLocalStreamStore) => getLocalTrackFromStreamValue($rawLocalStreamStore, "audio"),
);

export const rawLocalVideoTrackStore = derived<typeof rawLocalStreamStore, LocalTrackStoreValue>(
    rawLocalStreamStore,
    ($rawLocalStreamStore) => getLocalTrackFromStreamValue($rawLocalStreamStore, "video"),
);

/**
 * A store containing the audio track after optional noise suppression has been applied.
 * Updates are serialized so that the last enqueued update wins.
 */
let audioProcessedStreamGeneration = 0;
let audioProcessedStreamUpdateQueue: Promise<void> = Promise.resolve();

type SetAudioProcessedTrackIfCurrent = (value: LocalTrackStoreValue) => void;

export const audioProcessedLocalAudioTrackStore = derived<
    [typeof rawLocalAudioTrackStore, typeof customNoiseSuppressionActiveStore, typeof noiseSuppressionEngineStore],
    LocalTrackStoreValue
>(
    [rawLocalAudioTrackStore, customNoiseSuppressionActiveStore, noiseSuppressionEngineStore],
    ([$rawLocalAudioTrackStore, $customNoiseSuppressionActiveStore, $noiseSuppressionEngineStore], set) => {
        const myGen = ++audioProcessedStreamGeneration;
        const setIfCurrent: SetAudioProcessedTrackIfCurrent = (value) => {
            if (myGen === audioProcessedStreamGeneration) {
                set(value);
            }
        };

        currentAudioProcessedTransformAbortController?.abort(
            new AbortError("Noise suppression transform cancelled: new stream update"),
        );
        const controller = new AbortController();
        currentAudioProcessedTransformAbortController = controller;

        audioProcessedStreamUpdateQueue = audioProcessedStreamUpdateQueue
            .then(async () => {
                if ($rawLocalAudioTrackStore.type === "error") {
                    noiseSuppressionController.stop();
                    setIfCurrent($rawLocalAudioTrackStore);
                    return;
                }

                const processedTrack = noiseSuppressionController.transform(
                    $rawLocalAudioTrackStore.track,
                    $customNoiseSuppressionActiveStore,
                    $noiseSuppressionEngineStore,
                    controller.signal,
                );
                // Loading a noise suppression model takes up to a second or two: send the raw microphone meanwhile
                // rather than nothing. An already running pipeline answers at once and skips this.
                const settled = await Promise.race([
                    processedTrack.then(() => true),
                    new Promise<false>((resolve) => {
                        setTimeout(() => resolve(false), 50);
                    }),
                ]);
                if (!settled) {
                    setIfCurrent($rawLocalAudioTrackStore);
                }
                setIfCurrent({ type: "success", track: await processedTrack });
            })
            .catch((e) => {
                const isAbort = e instanceof AbortError || (e instanceof DOMException && e.name === "AbortError");
                if (isAbort) {
                    return;
                }
                console.error("Error in audio processed stream update queue", e);
                setIfCurrent({
                    type: "error",
                    error: e instanceof Error ? e : new Error("An unknown error happened"),
                });
            });
    },
    {
        type: "success",
        track: undefined,
    },
);

/**
 * Serializes local video track (background-transformed) updates so that the last enqueued update wins.
 */
let localStreamGeneration = 0;
let localStreamUpdateQueue: Promise<void> = Promise.resolve();

type SetLocalVideoTrackIfCurrent = (value: LocalTrackStoreValue) => void;

function stopBackgroundPipe(): void {
    backgroundTransformer?.stop();
    backgroundPipe = undefined;
}

async function runLocalVideoTrackUpdate(
    videoTrackValue: LocalTrackStoreValue,
    backgroundProcessingEnabled: boolean,
    setIfCurrent: SetLocalVideoTrackIfCurrent,
    signal: AbortSignal,
): Promise<void> {
    if (videoTrackValue.type === "error") {
        stopBackgroundPipe();
        setIfCurrent(videoTrackValue);
        return;
    }

    if (videoTrackValue.track === undefined || !backgroundProcessingEnabled) {
        stopBackgroundPipe();
        setIfCurrent(videoTrackValue);
        return;
    }

    if (!backgroundTransformer) {
        const currentConfig = get(backgroundConfigStore);
        const transformer = createBackgroundTransformer(
            currentConfig,
            (error) => {
                if (backgroundTransformer !== transformer) {
                    return;
                }

                console.warn("[MediaStore] Background transformer stopped after a terminal failure:", error);
                Sentry.captureException(error);
                warningMessageStore.addWarningMessage(get(LL).warning.backgroundProcessing.failedToApply());
                transformer.close();
                backgroundTransformer = undefined;
                backgroundPipe = undefined;
                lastBackgroundConfig = undefined;
                backgroundConfigStore.reset();
            },
            (sample) => analyticsClient.trackAdminEvent("media.background_effect.sample", sample),
        );
        backgroundTransformer = transformer;
    }

    if (!backgroundTransformer) {
        setIfCurrent(videoTrackValue);
        return;
    }

    if (videoTrackValue.track === backgroundPipe?.input && backgroundPipe.output?.readyState === "live") {
        setIfCurrent({ type: "success", track: backgroundPipe.output });
        return;
    }

    try {
        const finalStream = await backgroundTransformer.transform(new MediaStream([videoTrackValue.track]), signal);
        lastBackgroundConfig = { ...get(backgroundConfigStore) };
        const output = finalStream.getVideoTracks()[0];
        // Runs are serialized by localStreamUpdateQueue. A terminal failure in the meantime ends `output`, which
        // the reuse check above looks at.
        // eslint-disable-next-line require-atomic-updates
        backgroundPipe = { input: videoTrackValue.track, output };
        setIfCurrent({
            type: "success",
            track: output,
        });
    } catch (error) {
        const isAbort = error instanceof AbortError || (error instanceof DOMException && error.name === "AbortError");
        if (isAbort) {
            return;
        }
        if (error instanceof BackgroundProcessingUnsupportedError) {
            console.warn("[MediaStore] Background processing is not supported on this browser:", error.message);
            warningMessageStore.addWarningMessage(get(LL).warning.backgroundProcessing.notSupportedOnThisBrowser());
        } else {
            console.warn("[MediaStore] Failed to transform stream:", error);
            Sentry.captureException(error);
            warningMessageStore.addWarningMessage(get(LL).warning.backgroundProcessing.failedToApply());
        }
        backgroundConfigStore.reset();
        setIfCurrent({
            type: "error",
            error: error instanceof Error ? error : new Error("Background transform failed"),
        });
    }
}

export const backgroundProcessedLocalVideoTrackStore = derived<
    [typeof rawLocalVideoTrackStore, typeof backgroundProcessingEnabledStore],
    LocalTrackStoreValue
>(
    [rawLocalVideoTrackStore, backgroundProcessingEnabledStore],
    ([$rawLocalVideoTrackStore, $backgroundProcessingEnabled], set) => {
        const myGen = ++localStreamGeneration;
        const setIfCurrent: SetLocalVideoTrackIfCurrent = (value) => {
            if (myGen === localStreamGeneration) {
                set(value);
            }
        };

        currentTransformAbortController?.abort(new AbortError("Background transform cancelled: new stream update"));
        const controller = new AbortController();
        currentTransformAbortController = controller;

        localStreamUpdateQueue = localStreamUpdateQueue
            .catch((e) => {
                const isAbort = e instanceof AbortError || (e instanceof DOMException && e.name === "AbortError");
                if (isAbort) {
                    return;
                }
                console.error("Error in local stream update queue", e);
                setIfCurrent({
                    type: "error",
                    error: e instanceof Error ? e : new Error("An unknown error happened"),
                });
            })
            .then(() =>
                runLocalVideoTrackUpdate(
                    $rawLocalVideoTrackStore,
                    $backgroundProcessingEnabled,
                    setIfCurrent,
                    controller.signal,
                ),
            );
    },
    {
        type: "success",
        track: undefined,
    },
);

/**
 * The microphone state to broadcast to the space: true only when an audio track is actually
 * being captured/published. Unlike requestedMicrophoneState, this accounts for privacy shutdown,
 * energy saving, unavailable status, external services, and getUserMedia not having resolved yet.
 */
export const effectiveMicrophoneStateStore = derived(
    audioProcessedLocalAudioTrackStore,
    ($audioProcessedLocalAudioTrackStore) =>
        $audioProcessedLocalAudioTrackStore.type === "success" &&
        $audioProcessedLocalAudioTrackStore.track !== undefined,
);

/**
 * The camera state to broadcast to the space; symmetric to effectiveMicrophoneStateStore.
 */
export const effectiveCameraStateStore = derived(
    backgroundProcessedLocalVideoTrackStore,
    ($backgroundProcessedLocalVideoTrackStore) =>
        $backgroundProcessedLocalVideoTrackStore.type === "success" &&
        $backgroundProcessedLocalVideoTrackStore.track !== undefined,
);

export const localStreamStore = derived<
    [
        typeof audioProcessedLocalAudioTrackStore,
        typeof backgroundProcessedLocalVideoTrackStore,
        typeof rawLocalStreamStore,
    ],
    LocalStreamStoreValue
>(
    [audioProcessedLocalAudioTrackStore, backgroundProcessedLocalVideoTrackStore, rawLocalStreamStore],
    ([$audioProcessedLocalAudioTrackStore, $backgroundProcessedLocalVideoTrackStore, $rawLocalStreamStore]) =>
        composeLocalStreamValue(
            $audioProcessedLocalAudioTrackStore,
            $backgroundProcessedLocalVideoTrackStore,
            $rawLocalStreamStore.type === "success" && $rawLocalStreamStore.stream !== undefined,
        ),
);

/**
 * When (unavailable status + in background settings), we expose the local stream without video tracks
 * so that LiveKit/WebRTC do not publish camera; preview in BackgroundSettingsPanel still uses localStreamStore.
 */
export const localStreamStoreForPublishing = derived<
    [typeof localStreamStore, typeof availabilityStatusStore, typeof inBackgroundSettingsStore],
    LocalStreamStoreValue
>(
    [localStreamStore, availabilityStatusStore, inBackgroundSettingsStore],
    ([$localStreamStore, $availabilityStatusStore, $inBackgroundSettingsStore], set) => {
        const isUnavailableStatus =
            $availabilityStatusStore === AvailabilityStatus.DENY_PROXIMITY_MEETING ||
            $availabilityStatusStore === AvailabilityStatus.SILENT ||
            $availabilityStatusStore === AvailabilityStatus.DO_NOT_DISTURB ||
            $availabilityStatusStore === AvailabilityStatus.BACK_IN_A_MOMENT ||
            $availabilityStatusStore === AvailabilityStatus.SOUND_BLOCKED ||
            $availabilityStatusStore === AvailabilityStatus.BUSY;
        const shouldMaskVideoForPublishing = isUnavailableStatus && $inBackgroundSettingsStore;

        if (
            shouldMaskVideoForPublishing &&
            $localStreamStore.type === "success" &&
            $localStreamStore.stream &&
            $localStreamStore.stream.getVideoTracks().length > 0
        ) {
            const audioOnlyStream = new MediaStream($localStreamStore.stream.getAudioTracks());
            set({ type: "success", stream: audioOnlyStream });
            return;
        }
        set($localStreamStore);
    },
);

/**
 * Firefox does not support the OverconstrainedError class.
 * Instead, it throw an error whose name is "OverconstrainedError"
 */
interface OverconstrainedErrorInterface {
    constraint: string;
}
function isOverConstrainedError(e: unknown): e is OverconstrainedErrorInterface {
    return e instanceof Error && e.name === "OverconstrainedError";
}

function createVolumeStore(mediaStream: MediaStream | undefined, set: (value: number[] | undefined) => void) {
    if (mediaStream === undefined || mediaStream.getAudioTracks().length <= 0) {
        set(undefined);
        return;
    }

    const soundMeter = new SoundMeter(mediaStream);
    let error = false;

    const timeout = setInterval(() => {
        try {
            set(soundMeter.getVolume());
        } catch (err) {
            if (!error) {
                console.error(err);
                error = true;
            }
        }
    }, 100);

    return () => {
        clearInterval(timeout);
        soundMeter.stop();
    };
}

export const rawLocalVolumeStore = derived<typeof rawLocalAudioTrackStore, number[] | undefined>(
    rawLocalAudioTrackStore,
    ($rawLocalAudioTrackStore, set) => {
        if ($rawLocalAudioTrackStore.type === "error" || $rawLocalAudioTrackStore.track === undefined) {
            set(undefined);
            return;
        }

        return createVolumeStore(new MediaStream([$rawLocalAudioTrackStore.track]), set);
    },
    undefined,
);

export const localVolumeStore = derived<typeof localStreamStore, number[] | undefined>(
    localStreamStore,
    ($localStreamStoreValue, set) => {
        if ($localStreamStoreValue.type === "error") {
            set(undefined);
            return;
        }
        const mediaStream = $localStreamStoreValue.stream;

        return createVolumeStore(mediaStream, set);
    },
    undefined,
);

const talkIconVolumeThreshold = 10;

export const localVoiceIndicatorStore = derived<Readable<number[] | undefined>, boolean>(
    localVolumeStore,
    ($localVolumeStore) => {
        if ($localVolumeStore === undefined) {
            return false;
        }
        const volume = $localVolumeStore;
        if (volume === undefined) {
            return false;
        }
        const averageVolume = volume.reduce((a, b) => a + b, 0);
        return averageVolume > talkIconVolumeThreshold;
    },
    false,
);

/**
 * How long speech is held open across a silence before the period is closed. Longer
 * than the gaps inside speech, shorter than the gaps between turns.
 */
const SPEECH_HOLD_MS = 1500;

/**
 * Time the microphone was actually open, and time the user was actually speaking.
 *
 * Both ride the same interval machinery as `status.dwell`: the front says when a
 * period starts and stops, the pusher measures it on its own clock and emits one row
 * when it closes.
 *
 * The two signals are deliberately the local, effective ones:
 *
 * - the microphone is `effectiveMicrophoneStateStore`, true only while a track is
 *   really being captured. A denied permission, a privacy shutdown or an
 *   energy-saving pause read as closed, so this measures time a microphone could be
 *   heard rather than time a toggle was left on.
 * - speech is this user's OWN volume analyser, not a remote "who is speaking" signal.
 *   That keeps it independent of the meeting engine, and it means one speaker is
 *   reported once rather than once per listener. It is loudness, not recognition: no
 *   audio is inspected, transmitted or stored.
 */
let endMicrophoneDwell: EndTimedAnalyticsEvent | undefined;
let unsubscribeEffectiveMicrophone: Unsubscriber | undefined;
let unsubscribeVoiceIndicator: Unsubscriber | undefined;

// Both periods are opened once per meeting that hears them and cut at every meeting
// boundary — half of a period that straddles one happened in the meeting and half did
// not, and a row can only name one meeting. See CurrentMeeting.
const speechIntervals = createHeldIntervalTracker(
    () =>
        openTimedEventPerMeeting((context) =>
            analyticsClient.openTimedEvent("media.speech.dwell", context, { reopenOnReconnect: true }),
        ),
    SPEECH_HOLD_MS,
);

const closeMicrophoneDwell = (): void => {
    speechIntervals.stop();
    unsubscribeVoiceIndicator?.();
    unsubscribeVoiceIndicator = undefined;
    endMicrophoneDwell?.();
    endMicrophoneDwell = undefined;
};

const openMicrophoneDwell = (): void => {
    endMicrophoneDwell ??= openTimedEventPerMeeting((context) =>
        analyticsClient.openTimedEvent(
            "media.microphone.dwell",
            context,
            // The microphone did not close because the socket did: after a reconnect it
            // is still open, and nothing will say so again.
            { reopenOnReconnect: true },
        ),
    );

    // Same reasoning one level down: subscribing is what starts the SoundMeter, so
    // it runs only while there is actually something to hear.
    unsubscribeVoiceIndicator ??= localVoiceIndicatorStore.subscribe((speaking: boolean) =>
        speechIntervals.set(speaking),
    );
};

// Nothing here may be watched from module scope. `effectiveMicrophoneStateStore` is
// derived off the getUserMedia chain, so subscribing to it is what STARTS that chain:
// doing it at import time reaches for the microphone before the user has even reached
// a room, which changes when permission is asked for and when devices are held.
// `gameSceneIsLoadedStore` is a plain writable and costs nothing to watch, and it is
// true exactly while the user is in a room — which is also the only time an analytics
// row has a room to belong to.
// eslint-disable-next-line svelte/no-ignored-unsubscribe
gameSceneIsLoadedStore.subscribe((inRoom: boolean) => {
    if (!inRoom) {
        unsubscribeEffectiveMicrophone?.();
        unsubscribeEffectiveMicrophone = undefined;
        closeMicrophoneDwell();
        return;
    }

    unsubscribeEffectiveMicrophone ??= effectiveMicrophoneStateStore.subscribe((microphoneOpen: boolean) => {
        if (!microphoneOpen) {
            closeMicrophoneDwell();
            return;
        }

        openMicrophoneDwell();
    });
});

/**
 * Device list
 */
export const deviceListStore = readable<MediaDeviceInfo[] | undefined>(undefined, function start(set) {
    let deviceListCanBeQueried = false;

    const queryDeviceList = () => {
        // Note: so far, we are ignoring any failures.
        navigator.mediaDevices
            .enumerateDevices()
            .then((mediaDeviceInfos) => {
                set(mediaDeviceInfos);
                devicesNotLoaded.set(false);
            })
            .catch((e) => {
                console.error(e);
                devicesNotLoaded.set(false);
                throw e;
            });
    };

    const unsubscribe = localStreamStore.subscribe((streamResult) => {
        if (streamResult && streamResult.type === "success" && streamResult.stream !== undefined) {
            if (deviceListCanBeQueried === false) {
                queryDeviceList();
                deviceListCanBeQueried = true;
            }
        }
    });

    if (navigator.mediaDevices) {
        navigator.mediaDevices.addEventListener("devicechange", queryDeviceList);
    }

    return function stop() {
        unsubscribe();
        if (navigator.mediaDevices) {
            navigator.mediaDevices.removeEventListener("devicechange", queryDeviceList);
        }
    };
});

export const cameraListStore = derived(deviceListStore, ($deviceListStore) => {
    if ($deviceListStore === undefined) {
        return undefined;
    }

    return removeDuplicateDevices($deviceListStore.filter((device) => device.kind === "videoinput"));
});

/**
 * Context for the camera action-bar tooltip when the camera is off: permission denied vs no usable device.
 */
export const cameraButtonHelpContextStore = derived(
    [cameraAccessIssueStore, cameraListStore, devicesNotLoaded],
    ([issue, cameras, notLoaded]) => {
        if (issue === "permission_denied") {
            return "permission" as const;
        }
        if (issue === "no_device") {
            return "no_device" as const;
        }
        if (!notLoaded && cameras !== undefined && cameras.length === 0) {
            return "no_device" as const;
        }
        return null;
    },
);

export const microphoneListStore = derived(deviceListStore, ($deviceListStore) => {
    if ($deviceListStore === undefined) {
        return undefined;
    }

    return removeDuplicateDevices($deviceListStore.filter((device) => device.kind === "audioinput"));
});

/**
 * Context for the microphone action-bar tooltip when the mic is off: permission denied vs no usable device.
 */
export const microphoneButtonHelpContextStore = derived(
    [microphoneAccessIssueStore, microphoneListStore, devicesNotLoaded],
    ([issue, mics, notLoaded]) => {
        if (issue === "permission_denied") {
            return "permission" as const;
        }
        if (issue === "no_device") {
            return "no_device" as const;
        }
        if (mics !== undefined && mics.length === 0) {
            return "no_device" as const;
        }
        return null;
    },
);

export const speakerListStore = derived(deviceListStore, ($deviceListStore) => {
    if ($deviceListStore === undefined) {
        return undefined;
    }

    // Hidden wherever speakerSelectedStore ignores a selection (Safari, iOS): see AudioOutputStore
    if (!speakerSelectionSupported) {
        return;
    }

    return removeDuplicateDevices($deviceListStore.filter((device) => device.kind === "audiooutput"));
});

export const selectDefaultSpeaker = () => {
    const devices = get(speakerListStore);
    if (devices !== undefined && devices.length > 0) {
        speakerSelectedStore.set(devices[0].deviceId);
    } else {
        speakerSelectedStore.set("");
    }
};

// This is a singleton so no need to unsubscribe
//eslint-disable-next-line svelte/no-ignored-unsubscribe
speakerListStore.subscribe((devices) => {
    if (devices === undefined) {
        return;
    }
    // if the previous speaker used isn`t defined in the list, apply default speaker
    const previousSpeakerId = get(speakerSelectedStore);
    const previousAudioOutputDevice = devices.find((device) => device.deviceId === previousSpeakerId);
    if (previousAudioOutputDevice === undefined) {
        selectDefaultSpeaker();
    }
});

export { speakerSelectedStore };

let previousMediaDevices: MediaDeviceInfo[] | undefined = undefined;

// It is ok to not unsubscribe to this store because it is a singleton.
// eslint-disable-next-line svelte/no-ignored-unsubscribe
deviceListStore.subscribe((mediaDeviceInfos) => {
    if (mediaDeviceInfos === undefined) {
        return;
    }

    // check if the new list has the preferred device
    const preferredVideoInputDevice = localUserStore.getPreferredVideoInputDevice();
    const preferredAudioInputDevice = localUserStore.getPreferredAudioInputDevice();
    const preferredSpeakerDevice = localUserStore.getSpeakerDeviceId();

    if (preferredVideoInputDevice && mediaDeviceInfos.find((device) => device.deviceId === preferredVideoInputDevice)) {
        requestedCameraDeviceIdStore.set(preferredVideoInputDevice);
    }
    if (preferredAudioInputDevice && mediaDeviceInfos.find((device) => device.deviceId === preferredAudioInputDevice)) {
        requestedMicrophoneDeviceIdStore.set(preferredAudioInputDevice);
    }
    if (preferredSpeakerDevice && mediaDeviceInfos.find((device) => device.deviceId === preferredSpeakerDevice)) {
        speakerSelectedStore.set(preferredSpeakerDevice);
    }

    const thePreviousMediaDevices = previousMediaDevices;
    // get all media that not exist in the list
    if (thePreviousMediaDevices !== undefined) {
        // set the last new media devices detected (new devices detection)
        const newDevices = mediaDeviceInfos.filter(
            (device) => thePreviousMediaDevices.find((d) => d.deviceId === device.deviceId) === undefined,
        );
        lastNewMediaDeviceDetectedStore.set(newDevices);

        // Detect removed devices
        const removedDevices = thePreviousMediaDevices.filter(
            (device) => mediaDeviceInfos.find((d) => d.deviceId === device.deviceId) === undefined,
        );

        for (const removedDevice of removedDevices) {
            if (
                removedDevice.kind === "videoinput" &&
                currentStream?.getVideoTracks()[0]?.getSettings().deviceId === removedDevice.deviceId
            ) {
                if (get(requestedCameraDeviceIdStore) === undefined) {
                    // If we removed the default camera device, we retry (and ask for the new default camera that the OS will pick)
                    retryGetUserMedia(true, false);
                } else {
                    // If we removed a camera specifically requested, we retry without passing a device id.
                    requestedCameraDeviceIdStore.set(undefined);
                }
            } else if (
                removedDevice.kind === "audioinput" &&
                currentStream?.getAudioTracks()[0]?.getSettings().deviceId === removedDevice.deviceId
            ) {
                if (get(requestedMicrophoneDeviceIdStore) === undefined) {
                    // If we removed the default microphone device, we retry (and ask for the new default microphone that the OS will pick)
                    retryGetUserMedia(false, true);
                } else {
                    // If we removed a microphone specifically requested, we retry without passing a device id.
                    requestedMicrophoneDeviceIdStore.set(undefined);
                }
            }
        }
    }

    previousMediaDevices = [...mediaDeviceInfos];
});

function removeDuplicateDevices(devices: MediaDeviceInfo[]) {
    const uniqueDevices = new Map<string, MediaDeviceInfo>();
    devices.forEach((device) => {
        uniqueDevices.set(device.deviceId, device);
    });
    return Array.from(uniqueDevices.values());
}

// It is ok to not unsubscribe to this store because it is a singleton.
// eslint-disable-next-line svelte/no-ignored-unsubscribe
localStreamStore.subscribe((streamResult) => {
    if (streamResult && streamResult.type === "error") {
        if (streamResult.error.name === BrowserTooOldError.NAME || streamResult.error.name === WebviewOnOldIOS.NAME) {
            errorStore.addErrorMessage(streamResult.error);
        }
    }
});

// When the stream is initialized, the new sound constraint is recreated and the first speaker is set.
// If the user did not select the new speaker, the first new speaker cannot be selected automatically.
// It is ok to not unsubscribe to this store because it is a singleton.
// // eslint-disable-next-line svelte/no-ignored-unsubscribe
/*speakerSelectedStore.subscribe((speaker) => {
    const oldValue = localUserStore.getSpeakerDeviceId();
    const currentValue = speaker;
    const speakerList = get(speakerListStore);
    const oldDevice =
        oldValue && speakerList
            ? speakerList.find((mediaDeviceInfo) => mediaDeviceInfo.deviceId == oldValue)
            : undefined;
    if (
        oldDevice !== undefined &&
        speakerList !== undefined &&
        currentValue !== oldDevice.deviceId &&
        speakerList.find((value) => value.deviceId == oldValue)
    ) {
        console.warn("speakerSelectedStore.subscribe", oldValue, currentValue, oldDevice.deviceId);
        speakerSelectedStore.set(oldDevice.deviceId);
    }
});*/

function createVideoQualityStore() {
    const { subscribe, set } = writable<VideoQualitySetting>(localUserStore.getVideoQuality());

    return {
        subscribe,
        setQuality: (quality: VideoQualitySetting) => {
            set(quality);
            localUserStore.setVideoQuality(quality);
        },
    };
}

/**
 * A store containing the video quality setting.
 */
export const videoQualityStore = createVideoQualityStore();

export const lastNewMediaDeviceDetectedStore = writable<MediaDeviceInfo[]>([]);

/**
 * Subscribe to background config changes to update the transformer
 * This avoids recreating the entire stream when only parameters change
 */
// It is ok to not unsubscribe to this store because this module is a singleton.
// eslint-disable-next-line svelte/no-ignored-unsubscribe
backgroundConfigStore.subscribe(($config) => {
    // Skip if no transformer exists yet
    if (!backgroundTransformer || !lastBackgroundConfig) {
        return;
    }

    updateBackgroundProcessor({
        mode: $config.mode,
        blurAmount: $config.blurAmount,
        backgroundImage: $config.backgroundImage,
    });
});
